import { type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  BookOpen,
  Calendar,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  User,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { LinkText } from '@/components/LinkText';
import {
  MigratedCrudHistory,
  formatMigratedDateTime,
} from '@/components/MigratedCrudHistory';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
  kbaseApi,
  type KbaseEntry,
  type SaveKbaseEntryDto,
} from '@/api/kbase';

const FIELD_LABELS: Record<string, string> = {
  problem: 'Проблема',
  resolution: 'Решение',
  needsVectorSync: 'Ожидает AI-индексации',
  legacyId: 'Legacy ID',
  legacyLog: 'Legacy лог',
  legacyLastChange: 'Изменено',
  importedAt: 'Импортировано',
  createdAt: 'Создано',
  updatedAt: 'Изменено',
};

const HIDDEN_HISTORY_FIELDS = [
  'createdById',
  'updatedById',
  'legacyUserlistId',
  'legacyAuthorName',
  'legacyUsername',
  'legacySent',
  'legacyCreatedAt',
  'importedAt',
];

function formatDate(value?: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function authorName(entry: KbaseEntry): string {
  if (entry.updatedBy) return `${entry.updatedBy.firstName} ${entry.updatedBy.lastName}`;
  if (entry.createdBy) return `${entry.createdBy.firstName} ${entry.createdBy.lastName}`;
  return entry.legacyAuthorName || entry.legacyUsername || 'Неизвестно';
}

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    return response?.data?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function KbaseItemCard({
  item,
  onOpen,
}: {
  item: KbaseEntry;
  onOpen: (item: KbaseEntry) => void;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(item);
    }
  };

  return (
    <article
      className="rounded-lg border border-border bg-card p-3 active:bg-accent"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={handleKeyDown}
    >
      <h3 className="line-clamp-3 break-words text-sm font-medium">{item.problem}</h3>
      <div className="mt-2 line-clamp-3 whitespace-pre-wrap break-words text-sm text-muted-foreground">
        {item.resolution}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <User className="h-3 w-3" />
          {authorName(item)}
        </span>
        <span className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formatDate(item.updatedAt)}
        </span>
      </div>
    </article>
  );
}

function KbaseHistory({
  entry,
  open,
}: {
  entry: KbaseEntry;
  open: boolean;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['kbase-history', entry.id],
    queryFn: () => kbaseApi.getHistory(entry.id),
    enabled: open,
  });

  return (
    <MigratedCrudHistory
      legacyLog={entry.legacyLog}
      auditItems={data?.items || []}
      total={data?.total}
      isLoading={isLoading}
      error={error}
      fieldLabels={FIELD_LABELS}
      hiddenFields={HIDDEN_HISTORY_FIELDS}
    />
  );
}

function DetailField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;

  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase tracking-normal text-muted-foreground">
        {label}
      </div>
      <div className="whitespace-pre-wrap break-words text-sm">
        <LinkText>{value}</LinkText>
      </div>
    </div>
  );
}

function KbaseDetailSheet({
  entry,
  open,
  onOpenChange,
  onEdit,
}: {
  entry: KbaseEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (entry: KbaseEntry) => void;
}) {
  if (!entry) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader className="pr-8 text-left">
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            База знаний
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <Button className="w-full" onClick={() => onEdit(entry)}>
            <Pencil className="h-4 w-4" />
            Редактировать
          </Button>

          <div className="space-y-3 rounded-lg border border-border p-3">
            <DetailField label="Проблема" value={entry.problem} />
            <DetailField label="Решение" value={entry.resolution} />
          </div>

          <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3 text-xs">
            <span className="text-muted-foreground">Автор</span>
            <span className="text-right">{authorName(entry)}</span>
            <span className="text-muted-foreground">Создано</span>
            <span className="text-right">{formatMigratedDateTime(entry.createdAt)}</span>
            <span className="text-muted-foreground">Изменено</span>
            <span className="text-right">{formatMigratedDateTime(entry.updatedAt)}</span>
            {entry.needsVectorSync && (
              <>
                <span className="text-muted-foreground">AI индекс</span>
                <span className="text-right">ожидает обновления</span>
              </>
            )}
          </div>

          <KbaseHistory entry={entry} open={open} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function KbaseFormSheet({
  entry,
  open,
  onOpenChange,
  onSaved,
}: {
  entry: KbaseEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (entry: KbaseEntry) => void;
}) {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState('');
  const [resolution, setResolution] = useState('');

  useEffect(() => {
    if (!open) return;
    setProblem(entry?.problem || '');
    setResolution(entry?.resolution || '');
  }, [entry, open]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: SaveKbaseEntryDto = {
        problem: problem.trim(),
        resolution: resolution.trim(),
      };
      return entry ? kbaseApi.update(entry.id, body) : kbaseApi.create(body);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['kbase'] });
      queryClient.invalidateQueries({ queryKey: ['kbase-history', saved.id] });
      toast.success('Запись сохранена');
      onSaved(saved);
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error, 'Ошибка сохранения')),
  });

  const canSubmit =
    problem.trim().length > 0 && resolution.trim().length > 0 && !saveMutation.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader className="pr-8 text-left">
          <SheetTitle>{entry ? 'Редактировать запись' : 'Новая запись'}</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Проблема</label>
            <Textarea
              value={problem}
              onChange={(event) => setProblem(event.target.value)}
              rows={4}
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Решение</label>
            <Textarea
              value={resolution}
              onChange={(event) => setResolution(event.target.value)}
              rows={8}
            />
          </div>

          <div className="flex gap-2 border-t border-border pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={saveMutation.isPending}
            >
              Отмена
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={() => saveMutation.mutate()}
              disabled={!canSubmit}
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Сохранить
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function KbasePage() {
  const [search, setSearch] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<KbaseEntry | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formEntry, setFormEntry] = useState<KbaseEntry | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['kbase', search],
    queryFn: () => kbaseApi.getAll(search),
    staleTime: 5 * 60_000,
  });

  const entries = useMemo(() => data?.items || [], [data]);

  const openDetail = (entry: KbaseEntry) => {
    setSelectedEntry(entry);
    setDetailOpen(true);
  };

  const openCreate = () => {
    setFormEntry(null);
    setFormOpen(true);
  };

  const openEdit = (entry: KbaseEntry) => {
    setDetailOpen(false);
    setFormEntry(entry);
    setFormOpen(true);
  };

  const handleSaved = (entry: KbaseEntry) => {
    setSelectedEntry(entry);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="База знаний"
        showBack
        rightContent={
          <Button size="icon" aria-label="Добавить" onClick={openCreate}>
            <Plus className="h-5 w-5" />
          </Button>
        }
      />

      <main className="p-4">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Поиск"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-10 pr-10"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {!isLoading && !error && (
          <p className="mb-3 text-sm text-muted-foreground">
            Всего записей: {data?.meta.total ?? entries.length}
          </p>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="mb-4 h-12 w-12 text-destructive" />
            <p className="text-muted-foreground">Ошибка загрузки</p>
          </div>
        )}

        {!isLoading && !error && entries.length > 0 && (
          <div className="space-y-2">
            {entries.map((entry) => (
              <KbaseItemCard key={entry.id} item={entry} onOpen={openDetail} />
            ))}
          </div>
        )}

        {!isLoading && !error && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <BookOpen className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">База знаний пуста</p>
          </div>
        )}
      </main>

      <KbaseDetailSheet
        entry={selectedEntry}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEdit={openEdit}
      />

      <KbaseFormSheet
        entry={formEntry}
        open={formOpen}
        onOpenChange={setFormOpen}
        onSaved={handleSaved}
      />
    </div>
  );
}
